import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Droplets,
  Moon,
  Brain,
  Zap,
  Footprints,
  Utensils,
  HeartPulse,
  Flower2,
} from "lucide-react";
import { useHabitTrends } from "@/hooks/use-habit-analytics";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";

interface LifestyleTrendsChartProps {
  clientId: string;
  days?: number;
  className?: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  hydration: "#3B82F6",
  sleep: "#8B5CF6",
  stress: "#F97316",
  energy: "#EAB308",
  movement: "#22C55E",
  nutrition: "#10B981",
  recovery: "#EF4444",
  mindfulness: "#A855F7",
  custom: "#6B7280",
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  hydration: <Droplets className="h-4 w-4" />,
  sleep: <Moon className="h-4 w-4" />,
  stress: <Brain className="h-4 w-4" />,
  energy: <Zap className="h-4 w-4" />,
  movement: <Footprints className="h-4 w-4" />,
  nutrition: <Utensils className="h-4 w-4" />,
  recovery: <HeartPulse className="h-4 w-4" />,
  mindfulness: <Flower2 className="h-4 w-4" />,
};

function formatDate(dateStr: string) {
  try {
    return format(parseISO(dateStr), "MMM d");
  } catch {
    return dateStr;
  }
}

function calculateTrend(
  data: { completionRate: number }[]
): "improving" | "declining" | "stable" {
  if (data.length < 7) return "stable";

  const midpoint = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, midpoint);
  const secondHalf = data.slice(midpoint);

  const firstAvg =
    firstHalf.reduce((sum, d) => sum + d.completionRate, 0) / firstHalf.length;
  const secondAvg =
    secondHalf.reduce((sum, d) => sum + d.completionRate, 0) / secondHalf.length;

  if (secondAvg > firstAvg + 5) return "improving";
  if (secondAvg < firstAvg - 5) return "declining";
  return "stable";
}

export function LifestyleTrendsChart({
  clientId,
  days = 30,
  className,
}: LifestyleTrendsChartProps) {
  const { data, loading, error } = useHabitTrends(clientId, days);

  const chartData = useMemo(() => {
    return data.map((d) => ({
      date: formatDate(d.date),
      fullDate: d.date,
      completionRate: d.completionRate,
      completed: d.totalCompleted,
      total: d.totalAssigned,
    }));
  }, [data]);

  const trend = useMemo(() => calculateTrend(data), [data]);

  const avgCompletion = useMemo(() => {
    if (data.length === 0) return 0;
    return Math.round(
      data.reduce((sum, d) => sum + d.completionRate, 0) / data.length
    );
  }, [data]);

  if (loading) {
    return (
      <Card className={cn("", className)}>
        <CardHeader className="pb-2">
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="chart-height-sm w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <Card className={cn("", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-primary" />
            Habit Compliance Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center chart-height-sm text-muted-foreground">
            <Activity className="h-12 w-12 mb-2 opacity-20" />
            <p className="text-sm">No habit data available</p>
            <p className="text-xs">Assign habits to start tracking</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Habit Compliance Trends
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {trend === "improving" && (
                <TrendingUp className="h-4 w-4 text-success" />
              )}
              {trend === "declining" && (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )}
              {trend === "stable" && <Minus className="h-4 w-4 text-muted-foreground" />}
              <span className="capitalize">{trend}</span>
            </div>
            <Badge
              variant="outline"
              className={cn(
                "text-lg font-bold",
                avgCompletion >= 75
                  ? "text-success border-success/30"
                  : avgCompletion >= 50
                  ? "text-warning border-warning/30"
                  : "text-destructive border-destructive/30"
              )}
            >
              {avgCompletion}% avg
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div role="img" aria-label="Lifestyle habit completion trends over time">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="completionGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.18} />
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip
              cursor={false}
              animationDuration={150}
              content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="rounded-sm border border-border bg-card px-2 py-1.5 text-[13px] shadow-sm">
                    <div className="text-muted-foreground">{label}</div>
                    <div className="font-mono tabular-nums">{payload[0].value}% compliance</div>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="completionRate"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5}
              fill="url(#completionGradient)"
              name="completionRate"
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0, fill: "hsl(var(--primary))" }}
              animationDuration={350}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-2 mt-4 text-center text-xs">
          <div className="bg-muted/50 rounded p-2">
            <div className="text-muted-foreground">Days Tracked</div>
            <div className="font-semibold">{data.length}</div>
          </div>
          <div className="bg-info/10 rounded p-2">
            <div className="text-muted-foreground">Avg Daily</div>
            <div className="font-semibold text-info">{avgCompletion}%</div>
          </div>
          <div className="bg-success/10 rounded p-2">
            <div className="text-muted-foreground">Best Day</div>
            <div className="font-semibold text-success">
              {Math.max(...data.map((d) => d.completionRate))}%
            </div>
          </div>
          <div className="bg-warning/10 rounded p-2">
            <div className="text-muted-foreground">Lowest Day</div>
            <div className="font-semibold text-warning">
              {Math.min(...data.map((d) => d.completionRate))}%
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
